import CloseIcon from "@mui/icons-material/Close";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import OutlinedButton from "../../../../components/buttons/OutlinedButton";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { workflowsConditionsIcons } from "../../../stack/environment/icons/iconExport";
import {
  getAvailableActions,
  getAvailableConditions,
  TRIGGER_OPTIONS,
} from "../data/workflowOptions";
import ActionConfigForm from "./ActionConfigForm";
import AddMenuButton from "./AddMenuButton";
import ConditionSelector from "./ConditionSelector";
import OptionSelector from "./OptionSelector";
import WorkflowDetailsForm from "./WorkflowDetailsForm";
const SequentialWorkflowBuilder = ({
  setWorkflowName,
  setWorkflowDescription,
  workflowName = "",
  workflowDescription = "",
  selectedTrigger,
  selectedCondition,
  selectedConditions = [],
  selectedActions = [],
  onTriggerSelect,
  onConditionSelect,
  onConditionAdd,
  onConditionRemove,
  onActionAdd,
  onActionRemove,
  onActionsUpdate,
}) => {
  const colors = useColors();

  // Local state for UI interactions to prevent parent re-renders
  const [localSelectedConditions, setLocalSelectedConditions] =
    useState(selectedConditions);
  const [localSelectedActions, setLocalSelectedActions] =
    useState(selectedActions);
  const [expandedSteps, setExpandedSteps] = useState({
    when: !selectedTrigger,
    if: false,
    then: false,
  });
  const [showConditionSelector, setShowConditionSelector] = useState(false);
  const [showActionSelector, setShowActionSelector] = useState(false);
  const [actionConfigs, setActionConfigs] = useState({});
  const [expandedActions, setExpandedActions] = useState({});
  const [showInlineActionSelector, setShowInlineActionSelector] =
    useState(false);
  const [conditionsFinished, setConditionsFinished] = useState(false);
  const [actionContext, setActionContext] = useState("met"); // "met" or "not_met"

  // Sync local state with props when they change
  useEffect(() => {
    setLocalSelectedConditions(selectedConditions);
  }, [selectedConditions]);

  useEffect(() => {
    setLocalSelectedActions(selectedActions);

    // Initialize actionConfigs from existing configValues when actions are loaded
    if (selectedActions && selectedActions.length > 0) {
      const newConfigs = {};
      selectedActions.forEach((action) => {
        if (action.configValues) {
          newConfigs[action.id] = action.configValues;
        }
      });
      setActionConfigs((prev) => ({ ...prev, ...newConfigs }));
    }
  }, [selectedActions]);

  // Sync localSelectedActions back to parent when configs change
  useEffect(() => {
    if (onActionsUpdate && localSelectedActions.length > 0) {
      // Check if any actions have configValues that need to be synced
      const actionsWithConfigs = localSelectedActions.map((action) => ({
        ...action,
        configValues: actionConfigs[action.id] || action.configValues || {},
      }));

      // Only update if there are actual config changes
      const hasConfigChanges = actionsWithConfigs.some((action, index) => {
        const originalAction = selectedActions[index];
        return (
          originalAction &&
          JSON.stringify(action.configValues) !==
            JSON.stringify(originalAction.configValues)
        );
      });

      if (hasConfigChanges) {
        onActionsUpdate(actionsWithConfigs);
      }
    }
  }, [localSelectedActions, actionConfigs, onActionsUpdate, selectedActions]);

  const firstBoxRef = useRef(null);
  const lastBoxRef = useRef(null);
  const containerRef = useRef(null);
  const [linePosition, setLinePosition] = useState({ top: 0, height: 0 });

  useEffect(() => {
    // Only calculate line position if we have at least 2 boxes
    const hasMultipleBoxes =
      selectedTrigger &&
      (showConditionSelector ||
        selectedCondition ||
        localSelectedConditions.length > 0 ||
        showActionSelector ||
        localSelectedActions.length > 0);

    if (hasMultipleBoxes) {
      // Use setTimeout to ensure DOM has updated and refs are set
      let retryTimer = null;
      const timer = setTimeout(() => {
        const calculateLinePosition = () => {
          if (
            firstBoxRef.current &&
            lastBoxRef.current &&
            containerRef.current
          ) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const firstBoxRect = firstBoxRef.current.getBoundingClientRect();
            const lastBoxRect = lastBoxRef.current.getBoundingClientRect();

            const firstBoxCenter =
              firstBoxRect.top - containerRect.top + firstBoxRect.height / 2;
            const lastBoxCenter =
              lastBoxRect.top - containerRect.top + lastBoxRect.height / 2;

            const calculatedHeight = lastBoxCenter - firstBoxCenter;

            // Only show line if there are at least 2 different boxes
            if (calculatedHeight > 10) {
              setLinePosition({
                top: firstBoxCenter + 2,
                height: calculatedHeight,
              });
            } else {
              setLinePosition({ top: 0, height: 0 });
            }
            return true;
          }
          return false;
        };

        if (!calculateLinePosition()) {
          // If refs aren't ready yet, wait a bit more
          retryTimer = setTimeout(() => {
            calculateLinePosition();
          }, 100);
        }
      }, 50);

      return () => {
        clearTimeout(timer);
        if (retryTimer) {
          clearTimeout(retryTimer);
        }
      };
    } else {
      setLinePosition({ top: 0, height: 0 });
    }
  }, [
    selectedTrigger,
    selectedCondition,
    localSelectedConditions,
    localSelectedActions,
    expandedSteps,
    showConditionSelector,
    showActionSelector,
    expandedActions,
    showInlineActionSelector,
    conditionsFinished,
    actionContext,
  ]);

  const toggleStep = (stepType) => {
    setExpandedSteps((prev) => ({
      ...prev,
      [stepType]: !prev[stepType],
    }));
  };

  const handleCloseTrigger = () => {
    onTriggerSelect(null);
    setExpandedSteps({ when: true, if: false, then: false });
    setShowConditionSelector(false);
    setShowActionSelector(false);
  };

  const handleCloseCondition = () => {
    setLocalSelectedConditions([]);
    setConditionsFinished(false);
    if (onConditionSelect) onConditionSelect(null);
    setExpandedSteps((prev) => ({ ...prev, if: true, then: false }));
    setShowConditionSelector(false);
    setShowActionSelector(false);
  };

  const handleConditionAdd = (condition) => {
    const newConditions = [...localSelectedConditions, condition];
    setLocalSelectedConditions(newConditions);
    if (onConditionAdd) onConditionAdd(condition);
  };

  const handleConditionRemove = (conditionId) => {
    const newConditions = localSelectedConditions.filter(
      (c) => c.id !== conditionId
    );
    setLocalSelectedConditions(newConditions);
    setConditionsFinished(false); // Allow adding more conditions after removing one
    if (onConditionRemove) onConditionRemove(conditionId);
  };

  const handleActionConfigChange = useCallback((actionId, config) => {
    setActionConfigs((prev) => ({
      ...prev,
      [actionId]: config,
    }));

    // Also update the action object in localSelectedActions with the config values
    setLocalSelectedActions((prev) =>
      prev.map((action) =>
        action.id === actionId ? { ...action, configValues: config } : action
      )
    );
  }, []);

  const handleToggleActionExpand = useCallback((actionId) => {
    setExpandedActions((prev) => ({
      ...prev,
      [actionId]: !prev[actionId],
    }));
  }, []);

  const handleActionAdd = (action) => {
    const newActions = [...localSelectedActions, action];
    setLocalSelectedActions(newActions);
    onActionAdd(action); // Still notify parent for final state

    // Set the new action as expanded by default
    setExpandedActions((prev) => ({
      ...prev,
      [action.id]: true,
    }));
    // Hide the inline action selector after adding
    setShowInlineActionSelector(false);
  };

  const handleActionAddIfMet = () => {
    setActionContext("met");
    setShowActionSelector(true);
  };

  const handleActionAddIfNotMet = () => {
    setActionContext("not_met");
    setShowActionSelector(true);
  };

  const handleActionRemove = (actionId) => {
    const newActions = localSelectedActions.filter((a) => a.id !== actionId);
    setLocalSelectedActions(newActions);
    onActionRemove(actionId); // Still notify parent for final state
  };

  const handleAddMoreAction = () => {
    setShowInlineActionSelector(true);
  };

  const handleDoneWithActions = () => {
    // Collapse all actions to summary view
    const allActionIds = localSelectedActions.map((action) => action.id);
    const collapsedState = allActionIds.reduce((acc, id) => {
      acc[id] = false; // false means collapsed
      return acc;
    }, {});
    setExpandedActions(collapsedState);
    setShowInlineActionSelector(false);
  };

  const handleActionConfigured = (actionId) => {
    // Collapse this specific action after it's been configured
    setExpandedActions((prev) => ({
      ...prev,
      [actionId]: false,
    }));
  };

  const getStepIcon = (stepType) => {
    switch (stepType) {
      case "when":
        return workflowsConditionsIcons.When;
      case "if":
        return workflowsConditionsIcons.If;
      case "then":
        return workflowsConditionsIcons.Then;
      default:
        return workflowsConditionsIcons.When;
    }
  };

  const StepHeader = ({
    stepType,
    title,
    isActive,
    selectedItem,
    onClick,
    onClose,
    showClose = false,
  }) => (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        opacity: isActive ? 1 : 0.5,
        cursor: selectedItem ? "pointer" : "default",
        padding: "4px 0",
      }}
    >
      <Box
        sx={{
          backgroundColor: colors.common_white,
          borderRadius: "6px",
          padding: "6px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
          border: `1px solid ${colors.grey_20percent}`,
        }}
      >
        <SvgToIcon
          IconLogo={getStepIcon(stepType)}
          style={{
            width: "18px",
            height: "18px",
            color: colors.primary_main,
          }}
        />
      </Box>
      <Typography
        variant="subtitle2"
        sx={{
          color: colors.text_secondary,
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      {selectedItem && (
        <Typography
          variant="body2"
          sx={{
            color: colors.text_primary,
            marginLeft: 1,
            flex: 1,
          }}
        >
          {selectedItem.fullName || selectedItem.name}
        </Typography>
      )}
      {showClose && selectedItem && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{
            color: colors.text_secondary,
            "&:hover": {
              backgroundColor: colors.error_5percent,
              color: colors.error_main,
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  const StepBox = ({ children, hasHorizontalLine = false }) => (
    <Box
      sx={{
        position: "relative",
      }}
    >
      {hasHorizontalLine && (
        <Box
          sx={{
            position: "absolute",
            left: "-24px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "24px",
            height: "1px",
            backgroundColor: colors.grey_300,
          }}
        />
      )}
      {children}
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        paddingLeft: "24px",
        gap: "8px",
      }}
    >
      {/* Workflow Name and Description */}
      <WorkflowDetailsForm
        workflowName={workflowName}
        workflowDescription={workflowDescription}
        onWorkflowNameChange={setWorkflowName}
        onWorkflowDescriptionChange={setWorkflowDescription}
      />
      <Typography variant="caption" sx={{ color: colors.text_help }}>
        Pick an event to start this workflow
      </Typography>
      {/* Main vertical line on the left */}
      {selectedTrigger &&
      linePosition.height > 0 &&
      (showConditionSelector ||
        selectedCondition ||
        localSelectedConditions.length > 0 ||
        showActionSelector ||
        localSelectedActions.length > 0) ? (
        <Box
          sx={{
            position: "absolute",
            left: "0",
            top: `${linePosition.top}px`,
            height: `${linePosition.height}px`,
            width: "1px",
            backgroundColor: colors.grey_300 || "#CCCCCC",
            zIndex: 0,
          }}
        />
      ) : null}

      {/* Step 1: When (Trigger) */}
      <StepBox
        hasHorizontalLine={
          selectedTrigger &&
          (showConditionSelector ||
            selectedCondition ||
            localSelectedConditions.length > 0 ||
            showActionSelector ||
            localSelectedActions.length > 0)
        }
      >
        <Box
          ref={(el) => {
            firstBoxRef.current = el;
            // Set lastBoxRef only when trigger is the last visible box
            if (
              !showConditionSelector &&
              !selectedCondition &&
              localSelectedConditions.length === 0 &&
              !showActionSelector &&
              localSelectedActions.length === 0 &&
              !showInlineActionSelector
            ) {
              lastBoxRef.current = el;
            }
          }}
          sx={{
            padding: 2,
            backgroundColor: colors.common_white,
            boxShadow: `0 0 2px 0 ${colors.grey_48percent}`,
            borderRadius: "4px",
            marginBottom: "4px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <StepHeader
            stepType="when"
            title="When"
            isActive={true}
            selectedItem={selectedTrigger}
            onClick={() => selectedTrigger && toggleStep("when")}
            onClose={handleCloseTrigger}
            showClose={true}
          />

          <Collapse in={expandedSteps.when || !selectedTrigger}>
            <Box
              sx={{
                marginTop: selectedTrigger ? "8px" : "16px",
                marginLeft: "32px",
              }}
            >
              <OptionSelector
                options={TRIGGER_OPTIONS}
                onSelect={(option) => {
                  onTriggerSelect(option);
                  setExpandedSteps((prev) => ({
                    ...prev,
                    when: false,
                    if: true,
                  }));
                }}
                onCancel={() => {}}
                placeholder="select trigger event..."
              />
            </Box>
          </Collapse>
        </Box>
      </StepBox>

      {/* Add Menu - Show after trigger selection when no condition/action flow is active */}
      {selectedTrigger &&
      !showConditionSelector &&
      !selectedCondition &&
      localSelectedConditions.length === 0 &&
      !showActionSelector &&
      localSelectedActions.length === 0 ? (
        <AddMenuButton
          selectedCondition={selectedCondition}
          selectedConditions={localSelectedConditions}
          showConditionSelector={showConditionSelector}
          selectedActions={localSelectedActions}
          selectedTrigger={selectedTrigger}
          getAvailableActions={getAvailableActions}
          onAddCondition={() => setShowConditionSelector(true)}
          onAddAction={() => setShowActionSelector(true)}
          onAddActionIfMet={handleActionAddIfMet}
          onAddActionIfNotMet={handleActionAddIfNotMet}
        />
      ) : null}

      {/* Step 2: If (Condition) - Only show if showConditionSelector or conditions are selected */}
      {selectedTrigger &&
        (showConditionSelector || localSelectedConditions.length > 0) && (
          <StepBox hasHorizontalLine={true}>
            <Box
              ref={(el) => {
                // Set lastBoxRef when this is the last visible box
                // Either when selecting conditions OR when conditions exist and no actions
                if (el) {
                  if (
                    (showConditionSelector ||
                      localSelectedConditions.length > 0) &&
                    !showActionSelector &&
                    localSelectedActions.length === 0 &&
                    !showInlineActionSelector
                  ) {
                    lastBoxRef.current = el;
                  }
                }
              }}
              sx={{
                padding: 2,
                backgroundColor: colors.common_white,
                boxShadow: `0 0 2px 0 ${colors.grey_48percent}`,
                borderRadius: "4px",
                marginBottom: "4px",
                position: "relative",
                zIndex: 1,
              }}
            >
              <StepHeader
                stepType="if"
                title="If"
                isActive={true}
                selectedItem={
                  localSelectedConditions.length > 0
                    ? {
                        fullName: localSelectedConditions
                          .map((c) => c.fullName)
                          .join(" and "),
                      }
                    : null
                }
                onClick={() =>
                  localSelectedConditions.length > 0 && toggleStep("if")
                }
                onClose={handleCloseCondition}
                showClose={localSelectedConditions.length > 0}
              />

              <Collapse
                in={
                  expandedSteps.if ||
                  localSelectedConditions.length === 0 ||
                  (localSelectedConditions.length > 0 && !conditionsFinished)
                }
              >
                <Box
                  sx={{
                    marginTop:
                      localSelectedConditions.length > 0 ? "8px" : "16px",
                    marginLeft: "32px",
                  }}
                >
                  {/* Show selected conditions */}
                  {localSelectedConditions.length > 0 && (
                    <Box sx={{ marginBottom: 2 }}>
                      {localSelectedConditions.map((condition, index) => (
                        <Box
                          key={condition.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 12px",
                            backgroundColor: colors.grey_5percent,
                            border: `1px solid ${colors.grey_20percent}`,
                            borderRadius: "6px",
                            marginBottom: "8px",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {condition.fullName}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleConditionRemove(condition.id)}
                            sx={{
                              color: colors.text_secondary,
                              "&:hover": {
                                backgroundColor: colors.error_5percent,
                                color: colors.error_main,
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          marginTop: 2,
                          justifyContent: "flex-end",
                        }}
                      >
                        <OutlinedButton
                          onClick={() => {
                            setShowConditionSelector(true);
                            setConditionsFinished(false);
                          }}
                        >
                          Add More Conditions
                        </OutlinedButton>
                        <ContainedButton
                          onClick={() => {
                            setConditionsFinished(true);
                            setExpandedSteps((prev) => ({
                              ...prev,
                              if: false,
                              then: true,
                            }));
                          }}
                          color="primary"
                          size="small"
                        >
                          Done
                        </ContainedButton>
                      </Box>
                    </Box>
                  )}

                  {/* Condition selector */}
                  {(localSelectedConditions.length === 0 ||
                    showConditionSelector) && (
                    <ConditionSelector
                      conditionTypes={getAvailableConditions(selectedTrigger)}
                      onConditionSelect={(condition) => {
                        handleConditionAdd(condition);
                        setShowConditionSelector(false);
                        // Keep the If box open after adding a condition
                        // Don't automatically collapse it
                      }}
                      onCancel={() => {
                        setShowConditionSelector(false);
                      }}
                      placeholder="Select condition type..."
                    />
                  )}
                </Box>
              </Collapse>
            </Box>
          </StepBox>
        )}

      {/* Add Menu - Show after condition selection is finished */}
      {selectedTrigger &&
      localSelectedConditions.length > 0 &&
      conditionsFinished &&
      !showActionSelector &&
      localSelectedActions.length === 0 &&
      !showInlineActionSelector ? (
        <AddMenuButton
          selectedCondition={selectedCondition}
          selectedConditions={localSelectedConditions}
          showConditionSelector={showConditionSelector}
          selectedActions={localSelectedActions}
          selectedTrigger={selectedTrigger}
          getAvailableActions={getAvailableActions}
          onAddCondition={() => setShowConditionSelector(true)}
          onAddAction={() => setShowActionSelector(true)}
          onAddActionIfMet={handleActionAddIfMet}
          onAddActionIfNotMet={handleActionAddIfNotMet}
        />
      ) : null}

      {/* Conditions met section - Only show when there are conditions */}
      {selectedTrigger &&
        localSelectedConditions.length > 0 &&
        (showActionSelector ||
          localSelectedActions.length > 0 ||
          showInlineActionSelector) && (
          <Box
            sx={{ marginLeft: "32px", marginBottom: "8px", marginTop: "8px" }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.text_secondary,
                fontStyle: "italic",
              }}
            >
              {actionContext === "met"
                ? "If conditions are met"
                : "If conditions are not met"}
            </Typography>
          </Box>
        )}

      {/* Step 3: Then (Actions) - Show if there are conditions OR if actions are added directly after trigger */}
      {selectedTrigger &&
        (localSelectedConditions.length > 0 ||
          (!localSelectedConditions.length && !showConditionSelector)) &&
        (showActionSelector ||
          localSelectedActions.length > 0 ||
          showInlineActionSelector) && (
          <>
            {/* Render each action in its own container */}
            {localSelectedActions.map((action, index) => (
              <StepBox key={action.id} hasHorizontalLine={true}>
                <Box
                  ref={(el) => {
                    // Set lastBoxRef to the last action box
                    if (
                      index === localSelectedActions.length - 1 &&
                      !showInlineActionSelector
                    ) {
                      lastBoxRef.current = el;
                    }
                  }}
                  sx={{
                    padding: 2,
                    backgroundColor: colors.common_white,
                    boxShadow: `0 0 2px 0 ${colors.grey_48percent}`,
                    borderRadius: "4px",
                    marginBottom: "4px",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <StepHeader
                    stepType="then"
                    title={index === 0 ? "Then" : "And"}
                    isActive={true}
                    selectedItem={{ name: action.name }}
                    onClick={() => handleToggleActionExpand(action.id)}
                    onClose={() => onActionRemove(action.id)}
                    showClose={true}
                  />

                  <Collapse in={expandedActions[action.id] !== false}>
                    <Box sx={{ marginTop: "16px", marginLeft: "32px" }}>
                      <ActionConfigForm
                        action={action}
                        config={{
                          ...(action.configValues || {}),
                          ...(actionConfigs[action.id] || {}),
                        }}
                        onConfigChange={handleActionConfigChange}
                        onClose={() => handleActionRemove(action.id)}
                        isExpanded={expandedActions[action.id] !== false}
                        onToggleExpand={handleToggleActionExpand}
                        onAddMoreAction={handleAddMoreAction}
                        onDone={handleDoneWithActions}
                        onActionConfigured={handleActionConfigured}
                        showActionButtons={
                          index === localSelectedActions.length - 1 &&
                          !showInlineActionSelector
                        }
                      />
                    </Box>
                  </Collapse>
                </Box>
              </StepBox>
            ))}

            {/* Inline action selector in its own container */}
            {showInlineActionSelector && (
              <StepBox hasHorizontalLine={true}>
                <Box
                  ref={lastBoxRef}
                  sx={{
                    padding: 2,
                    backgroundColor: colors.common_white,
                    boxShadow: `0 0 2px 0 ${colors.grey_48percent}`,
                    borderRadius: "4px",
                    marginBottom: "4px",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <OptionSelector
                    options={getAvailableActions(
                      localSelectedConditions.length > 0
                        ? localSelectedConditions[0]
                        : selectedTrigger
                    ).filter(
                      (a) => !localSelectedActions.find((sa) => sa.id === a.id)
                    )}
                    onSelect={handleActionAdd}
                    onCancel={() => setShowInlineActionSelector(false)}
                    placeholder="Select action..."
                  />
                </Box>
              </StepBox>
            )}

            {/* Initial action selector when no actions exist */}
            {localSelectedActions.length === 0 && !showInlineActionSelector && (
              <StepBox hasHorizontalLine={true}>
                <Box
                  ref={lastBoxRef}
                  sx={{
                    padding: 2,
                    backgroundColor: colors.common_white,
                    boxShadow: `0 0 2px 0 ${colors.grey_48percent}`,
                    borderRadius: "4px",
                    marginBottom: "4px",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <StepHeader
                    stepType="then"
                    title="Then"
                    isActive={true}
                    selectedItem={null}
                    onClick={() => {}}
                    onClose={() => {}}
                    showClose={false}
                  />
                  <Box sx={{ marginTop: "16px", marginLeft: "32px" }}>
                    <OptionSelector
                      options={getAvailableActions(
                        localSelectedConditions.length > 0
                          ? localSelectedConditions[0]
                          : selectedTrigger
                      )}
                      onSelect={handleActionAdd}
                      onCancel={() => {}}
                      placeholder="Select action..."
                    />
                  </Box>
                </Box>
              </StepBox>
            )}
          </>
        )}
    </Box>
  );
};

export default SequentialWorkflowBuilder;
